import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import documentsService from '@/services/documentsService';
import { useToast } from '@/components/notifications/ToastProvider';
import { 
  Home,
  Calendar,
  DollarSign,
  TrendingUp,
  Zap,
  FileText,
  Upload,
  CheckCircle,
  Clock,
  AlertTriangle,
  Leaf,
  Thermometer,
  Wrench,
  Building,
  Calculator,
  Users,
  PieChart,
  BarChart3,
  TrendingDown,
  Euro,
  Percent,
  FileDown,
  Mail,
  Receipt,
  Plus,
  Scale,
  Shield,
  Target,
  Banknote,
  CreditCard,
  LineChart
} from 'lucide-react';

const SanierungsTab = () => {
  const [selectedProperty, setSelectedProperty] = useState('draisstrasse-26');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Upload states
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const { showSuccess, showError } = useToast();

  // Property UUID Mapping - ECHTE UUIDs aus der Datenbank
  const propertyUUIDs = {
    'draisstrasse-26': 'e963c246-7572-4bf6-88b2-c2a56a362a0b',
    'bgm-fuchs-str-17': 'd0d19e39-8ba5-49f9-95e2-97670567cbcc',
    'waldhofstrasse-76': 'waldhof-uuid-placeholder', // TODO: Echte UUID aus DB
    'scheffelstrasse-10': 'dc849495-81d2-487d-b1e7-394ca3403da3',
    'hafenpark': '8c16ffc6-a2e3-4c74-a799-0c45dc3ba0a2',
    'mainzerstrasse-120': '8dccc591-2305-44f7-aa3a-401fd3df5254',
    'neckarauer-strasse-245': 'df8d4c41-f730-469a-bc2e-884b96fc7f92'
  };

  // Sanierungsgebiet-Erkennung mit Laufzeit-Information
  const sanierungsgebiete = {
    'draisstrasse-26': {
      name: 'Neckarstadt-West',
      steuervorteile: true,
      zusatzfoerderung: 0.05, // 5% Mannheim-Bonus
      afa_erhoeht: 0.09, // 9% statt 2%
      startjahr: 2018,
      laufzeit: 15, // Jahre
      endjahr: 2033
    },
    'bgm-fuchs-str-17': {
      name: 'Neckarstadt-West', 
      steuervorteile: true,
      zusatzfoerderung: 0.05,
      afa_erhoeht: 0.09,
      startjahr: 2018,
      laufzeit: 15,
      endjahr: 2033
    },
    'waldhofstrasse-76': {
      name: 'Neckarstadt-West',
      steuervorteile: true,
      zusatzfoerderung: 0.05,
      afa_erhoeht: 0.09,
      startjahr: 2018,
      laufzeit: 15,
      endjahr: 2033
    },
    'scheffelstrasse-10': {
      name: 'Ilvesheim Ortskern',
      steuervorteile: true,
      zusatzfoerderung: 0.03, // 3% Ilvesheim-Bonus
      afa_erhoeht: 0.09,
      startjahr: 2020,
      laufzeit: 6, // Jahre (bis Ende 2026)
      endjahr: 2026
    }
  };

  const currentSanierungsgebiet = sanierungsgebiete[selectedProperty];

  // Erweiterte Sanierungsdaten - NUR für Draisstraße 26 (echte Daten)
  const sanierungsDaten = {
    'draisstrasse-26': {
      objektName: 'Draisstraße 26, Mannheim',
      objektTyp: '8-Familienhaus',
      baujahr: 1910,
      wohnflaeche: 406,
      anzahlWohnungen: 8,
      durchschnittsMiete: 8.50, // €/m²
      gesamtMieteinnahmen: 27612, // €/Jahr
      istZustand: {
        energiekosten: 9600,
        primärenergiebedarf: 208,
        co2Emission: 54,
        endenergieverbrauch: 93800,
        effizienzklasse: 'H'
      },
      zielZustand: {
        energiekosten: 4450,
        primärenergiebedarf: 67,
        co2Emission: 5,
        endenergieverbrauch: 38900,
        effizienzklasse: 'EH 70'
      },
      massnahmenpakete: [
        {
          id: 1,
          titel: 'Kellertür / Boden / Wände Kellerabgang',
          zeitraum: 'ca. 2026',
          investitionskosten: 9400,
          sowiesoKosten: 600,
          foerderung: 1880,
          energiekostenNach: 9250,
          status: 'geplant',
          beschreibung: 'Dämmung der Kellertür und Kellerabgang, Verbesserung der Wärmedämmung im Kellerbereich',
          modernisierungskosten: 8800, // Investitionskosten - Sowieso-Kosten
          umlagefaehig: 8800 * 0.08, // 8% p.a.
          mieterhöhungProM2: (8800 * 0.08) / 406 / 12 // pro m² pro Monat
        },
        {
          id: 2,
          titel: 'Dach / OGD / Gaubenwand / Innenwand / Gaubenfenster',
          zeitraum: 'ca. 2027',
          investitionskosten: 102000,
          sowiesoKosten: 76300,
          foerderung: 20400,
          energiekostenNach: 7800,
          status: 'geplant',
          beschreibung: 'Umfassende Dachsanierung mit Dämmung, Erneuerung der Gaubenfenster und Innenwände',
          modernisierungskosten: 25700, // Investitionskosten - Sowieso-Kosten
          umlagefaehig: 25700 * 0.08,
          mieterhöhungProM2: (25700 * 0.08) / 406 / 12
        },
        {
          id: 3,
          titel: 'Außenwand / Fenster / Tür / Lüftung',
          zeitraum: 'ca. 2028',
          investitionskosten: 165000,
          sowiesoKosten: 78800,
          foerderung: 33000,
          energiekostenNach: 4500,
          status: 'geplant',
          beschreibung: 'Fassadendämmung, neue Fenster und Türen, Installation einer kontrollierten Wohnraumlüftung',
          modernisierungskosten: 86200,
          umlagefaehig: 86200 * 0.08,
          mieterhöhungProM2: (86200 * 0.08) / 406 / 12
        },
        {
          id: 4,
          titel: 'Heizung / Warmwasser',
          zeitraum: 'ca. 2030',
          investitionskosten: 16500,
          sowiesoKosten: 13000,
          foerderung: 4950,
          energiekostenNach: 4450,
          status: 'geplant',
          beschreibung: 'Installation einer effizienten Wärmepumpe mit Warmwasseraufbereitung',
          modernisierungskosten: 3500,
          umlagefaehig: 3500 * 0.08,
          mieterhöhungProM2: (3500 * 0.08) / 406 / 12
        }
      ],
      gesamtInvestition: 292900,
      gesamtFoerderung: 60230,
      jaehrlicheEinsparung: 5150,
      finanzierung: {
        eigenkapitalAnteil: 0.25,
        fremdkapitalZins: 0.038,
        tilgung: 0.02
      },
      steuerlicheAspekte: {
        afaSatz: 0.09, // 9% für Sanierungsgebiet
        grenzsteuersatz: 0.42 // 42%
      }
    }
    // Andere Objekte haben KEINE Daten - das ist OK!
  };

  // DEFENSIVE PROGRAMMIERUNG - Prüfe ob Daten vorhanden
  const daten = sanierungsDaten[selectedProperty];
  const hasSanierungsdaten = daten && daten.massnahmenpakete && daten.massnahmenpakete.length > 0;

  // Berechnungen - NUR wenn Daten vorhanden
  let gesamtModernisierungskosten = 0;
  let gesamtUmlagefaehig = 0;
  let mieterhöhungGesamt = 0;
  let mieterhöhungProM2Monat = 0;
  let betriebskostensenkung = 0;
  let eigenkapital = 0;
  let fremdkapital = 0;
  let jaehrlicheZinsen = 0;
  let jaehrlicheTilgung = 0;
  let jaehrlicheKapitalkosten = 0;
  let steuerlicheErsparnis = 0;
  let nettoInvestition = 0;

  if (hasSanierungsdaten) {
    gesamtModernisierungskosten = daten.massnahmenpakete.reduce((sum, m) => sum + m.modernisierungskosten, 0);
    gesamtUmlagefaehig = daten.massnahmenpakete.reduce((sum, m) => sum + m.umlagefaehig, 0);
    mieterhöhungGesamt = gesamtUmlagefaehig / 12; // pro Monat
    mieterhöhungProM2Monat = mieterhöhungGesamt / daten.wohnflaeche;
    betriebskostensenkung = daten.jaehrlicheEinsparung / daten.wohnflaeche / 12; // pro m² pro Monat
    eigenkapital = daten.gesamtInvestition * daten.finanzierung.eigenkapitalAnteil;
    fremdkapital = daten.gesamtInvestition - eigenkapital;
    jaehrlicheZinsen = fremdkapital * daten.finanzierung.fremdkapitalZins;
    jaehrlicheTilgung = fremdkapital * daten.finanzierung.tilgung;
    jaehrlicheKapitalkosten = jaehrlicheZinsen + jaehrlicheTilgung;
    steuerlicheErsparnis = (daten.gesamtInvestition * daten.steuerlicheAspekte.afaSatz) * daten.steuerlicheAspekte.grenzsteuersatz;
    nettoInvestition = daten.gesamtInvestition - daten.gesamtFoerderung - steuerlicheErsparnis;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'abgeschlossen': return 'text-green-600 bg-green-100';
      case 'in-arbeit': return 'text-blue-600 bg-blue-100';
      case 'geplant': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'abgeschlossen': return <CheckCircle className="h-4 w-4" />;
      case 'in-arbeit': return <Wrench className="h-4 w-4" />;
      case 'geplant': return <Clock className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  // Upload functionality - EXAKTE KOPIE aus funktionierender DocumentsPage
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress(Math.round(((i + 0.5) / files.length) * 100));

        const result = await documentsService.uploadDocument(
          file,
          propertyUUIDs[selectedProperty] || selectedProperty,
          'Sanierungsfahrplan',
          `Sanierungsfahrplan für ${selectedProperty}`
        );

        if (result.success) {
          showSuccess(`${file.name} erfolgreich hochgeladen`);
        } else {
          showError(`Fehler beim Hochladen von ${file.name}: ${result.error}`);
        }

        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }
    } catch (error) {
      console.error('Upload-Fehler:', error);
      showError('Fehler beim Hochladen der Dateien');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  // Property-Namen für Dropdown
  const propertyNames = {
    'draisstrasse-26': 'Draisstraße 26, Mannheim',
    'bgm-fuchs-str-17': 'Bürgermeister-Fuchs-Str. 17, Mannheim',
    'waldhofstrasse-76': 'Waldhofstraße 76, Mannheim',
    'scheffelstrasse-10': 'Scheffelstraße 10, Ilvesheim',
    'hafenpark': 'Hafenpark, Mannheim',
    'mainzerstrasse-120': 'Mainzerstraße 120, Mannheim',
    'neckarauer-strasse-245': 'Neckarauer Straße 245, Mannheim'
  };

  // Upload-Interface für Objekte ohne Sanierungsdaten
  const renderUploadInterface = () => (
    <div className="space-y-6">
      {/* Sanierungsgebiet-Info falls vorhanden */}
      {currentSanierungsgebiet && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="h-5 w-5 text-green-600" />
              <h3 className="font-semibold text-green-800">Sanierungsgebiet erkannt</h3>
            </div>
            <p className="text-sm text-green-700 mb-2">
              Diese Immobilie liegt im Sanierungsgebiet <strong>{currentSanierungsgebiet.name}</strong>
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Erhöhte AfA:</span> {(currentSanierungsgebiet.afa_erhoeht * 100).toFixed(0)}% p.a.
              </div>
              <div>
                <span className="font-medium">Laufzeit:</span> bis {currentSanierungsgebiet.endjahr}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload-Bereich */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="h-5 w-5" />
            <span>Sanierungsfahrplan hochladen</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Sanierungsfahrplan für {propertyNames[selectedProperty]}
            </h3>
            <p className="text-gray-600 mb-4">
              Laden Sie den Sanierungsfahrplan hoch, um eine detaillierte Analyse zu erhalten
            </p>
            
            <div className="space-y-4">
              <Button
                onClick={() => document.getElementById('file-upload').click()}
                disabled={isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Wird hochgeladen... {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Dateien auswählen
                  </>
                )}
              </Button>
              
              <input
                id="file-upload"
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <p className="text-xs text-gray-500">
                Unterstützte Formate: PDF, Word, Bilder (max. 10MB pro Datei)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info-Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-2 mb-2">
            <FileText className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-blue-800">Was passiert nach dem Upload?</h3>
          </div>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Automatische OCR-Erkennung der Sanierungsmaßnahmen</li>
            <li>• KI-gestützte Analyse der Kosten und Förderungen</li>
            <li>• Berechnung der Wirtschaftlichkeit und Mietumlagen</li>
            <li>• Integration in Ihr Investment Cockpit</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header mit Objektauswahl */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sanierungsfahrplan</h2>
          <p className="text-gray-600">Energetische Modernisierung und Wirtschaftlichkeitsanalyse</p>
        </div>
        
        <div className="w-80">
          <Select value={selectedProperty} onValueChange={setSelectedProperty}>
            <SelectTrigger className="bg-white border-gray-300 shadow-sm"> {/* FIXED: Weißer Hintergrund */}
              <SelectValue placeholder="Objekt auswählen" />
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-300 shadow-lg"> {/* FIXED: Weißer Hintergrund */}
              {Object.entries(propertyNames).map(([key, name]) => (
                <SelectItem key={key} value={key} className="hover:bg-gray-50">
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Conditional Rendering basierend auf verfügbaren Daten */}
      {!hasSanierungsdaten ? (
        // Kein Sanierungsfahrplan vorhanden - Upload-Interface
        renderUploadInterface()
      ) : (
        // Sanierungsfahrplan vorhanden - Vollständige Analyse
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Übersicht</TabsTrigger>
            <TabsTrigger value="measures">Maßnahmen</TabsTrigger>
            <TabsTrigger value="economics">Wirtschaftlichkeit</TabsTrigger>
            <TabsTrigger value="timeline">Zeitplan</TabsTrigger>
          </TabsList>

          {/* Übersicht Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Objektinformationen */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center">
                    <Home className="h-5 w-5 mr-2" />
                    Objektdaten
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Baujahr:</span>
                    <span className="font-medium">{daten.baujahr}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Wohnfläche:</span>
                    <span className="font-medium">{daten.wohnflaeche.toLocaleString()} m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Wohnungen:</span>
                    <span className="font-medium">{daten.anzahlWohnungen}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ø Miete:</span>
                    <span className="font-medium">{daten.durchschnittsMiete.toFixed(2)} €/m²</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center">
                    <DollarSign className="h-5 w-5 mr-2" />
                    Investition
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gesamtinvestition:</span>
                    <span className="font-medium">{daten.gesamtInvestition.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Förderung:</span>
                    <span className="font-medium text-green-600">-{daten.gesamtFoerderung.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Steuerersparnis:</span>
                    <span className="font-medium text-green-600">-{steuerlicheErsparnis.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-medium">Netto-Investition:</span>
                    <span className="font-bold">{nettoInvestition.toLocaleString()} €</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    Einsparungen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Jährlich:</span>
                    <span className="font-medium text-green-600">{daten.jaehrlicheEinsparung.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pro m²/Monat:</span>
                    <span className="font-medium">{betriebskostensenkung.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amortisation:</span>
                    <span className="font-medium">{Math.round(nettoInvestition / daten.jaehrlicheEinsparung)} Jahre</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sanierungsgebiet-Info */}
            {currentSanierungsgebiet && (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Shield className="h-5 w-5 text-green-600" />
                      <h3 className="font-semibold text-green-800">Sanierungsgebiet {currentSanierungsgebiet.name}</h3>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-green-700">Erhöhte AfA: {(currentSanierungsgebiet.afa_erhoeht * 100).toFixed(0)}% p.a.</div>
                      <div className="text-sm text-green-700">Läuft bis: {currentSanierungsgebiet.endjahr}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Energieeffizienz Vergleich */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Zap className="h-5 w-5 mr-2" />
                  Energieeffizienz-Verbesserung
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Ist-Zustand</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Energiekosten/Jahr:</span>
                        <span className="font-medium">{daten.istZustand.energiekosten.toLocaleString()} €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Effizienzklasse:</span>
                        <span className="font-medium bg-red-100 text-red-800 px-2 py-1 rounded text-sm">
                          {daten.istZustand.effizienzklasse}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">CO₂-Emission:</span>
                        <span className="font-medium">{daten.istZustand.co2Emission} kg/m²a</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Ziel-Zustand</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Energiekosten/Jahr:</span>
                        <span className="font-medium text-green-600">{daten.zielZustand.energiekosten.toLocaleString()} €</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Effizienzklasse:</span>
                        <span className="font-medium bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                          {daten.zielZustand.effizienzklasse}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">CO₂-Emission:</span>
                        <span className="font-medium text-green-600">{daten.zielZustand.co2Emission} kg/m²a</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maßnahmen Tab */}
          <TabsContent value="measures" className="space-y-6">
            <div className="grid gap-4">
              {daten.massnahmenpakete.map((massnahme, index) => (
                <Card key={massnahme.id} className="relative">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2.5 py-0.5 rounded">
                            Phase {massnahme.id}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(massnahme.status)}`}>
                            {getStatusIcon(massnahme.status)}
                            <span className="ml-1 capitalize">{massnahme.status}</span>
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {massnahme.titel}
                        </h3>
                        <p className="text-gray-600 mb-4">
                          {massnahme.beschreibung}
                        </p>
                      </div>
                      <div className="text-right ml-4">
                        <div className="text-sm text-gray-500">{massnahme.zeitraum}</div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Investitionskosten</div>
                        <div className="font-semibold">{massnahme.investitionskosten.toLocaleString()} €</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Förderung</div>
                        <div className="font-semibold text-green-600">-{massnahme.foerderung.toLocaleString()} €</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Modernisierungskosten</div>
                        <div className="font-semibold">{massnahme.modernisierungskosten.toLocaleString()} €</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Mieterhöhung</div>
                        <div className="font-semibold">+{massnahme.mieterhöhungProM2.toFixed(2)} €/m²</div>
                      </div>
                    </div>
                  </CardContent>
                  {index < daten.massnahmenpakete.length - 1 && (
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-0.5 h-4 bg-gray-300"></div>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Wirtschaftlichkeit Tab */}
          <TabsContent value="economics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Calculator className="h-5 w-5 mr-2" />
                    Finanzierung
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Eigenkapital ({(daten.finanzierung.eigenkapitalAnteil * 100).toFixed(0)}%):</span>
                    <span className="font-medium">{eigenkapital.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fremdkapital:</span>
                    <span className="font-medium">{fremdkapital.toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Zinssatz:</span>
                    <span className="font-medium">{(daten.finanzierung.fremdkapitalZins * 100).toFixed(1)}% p.a.</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tilgung:</span>
                    <span className="font-medium">{(daten.finanzierung.tilgung * 100).toFixed(1)}% p.a.</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-medium">Jährliche Kapitalkosten:</span>
                    <span className="font-bold">{jaehrlicheKapitalkosten.toLocaleString()} €</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Scale className="h-5 w-5 mr-2" />
                    Steuerliche Aspekte
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">AfA-Satz:</span>
                    <span className="font-medium">{(daten.steuerlicheAspekte.afaSatz * 100).toFixed(0)}% p.a.</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Grenzsteuersatz:</span>
                    <span className="font-medium">{(daten.steuerlicheAspekte.grenzsteuersatz * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Jährliche AfA:</span>
                    <span className="font-medium">{(daten.gesamtInvestition * daten.steuerlicheAspekte.afaSatz).toLocaleString()} €</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-medium">Steuerersparnis p.a.:</span>
                    <span className="font-bold text-green-600">{steuerlicheErsparnis.toLocaleString()} €</span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Mieterhöhung */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Mieterhöhungspotenzial
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <div className="text-sm text-gray-500">Umlagefähige Kosten</div>
                    <div className="text-2xl font-bold">{gesamtUmlagefaehig.toLocaleString()} €</div>
                    <div className="text-sm text-gray-500">pro Jahr</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Mieterhöhung gesamt</div>
                    <div className="text-2xl font-bold text-green-600">+{mieterhöhungGesamt.toLocaleString()} €</div>
                    <div className="text-sm text-gray-500">pro Monat</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Pro m² und Monat</div>
                    <div className="text-2xl font-bold text-green-600">+{mieterhöhungProM2Monat.toFixed(2)} €</div>
                    <div className="text-sm text-gray-500">Durchschnitt</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Zeitplan Tab */}
          <TabsContent value="timeline" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Sanierungszeitplan
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {daten.massnahmenpakete.map((massnahme, index) => (
                    <div key={massnahme.id} className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(massnahme.status)}`}>
                          {getStatusIcon(massnahme.status)}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-lg font-medium text-gray-900">
                            {massnahme.titel}
                          </h4>
                          <span className="text-sm text-gray-500">{massnahme.zeitraum}</span>
                        </div>
                        <p className="text-gray-600 mb-3">{massnahme.beschreibung}</p>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Investition:</span>
                            <span className="ml-2 font-medium">{massnahme.investitionskosten.toLocaleString()} €</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Förderung:</span>
                            <span className="ml-2 font-medium text-green-600">-{massnahme.foerderung.toLocaleString()} €</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Status:</span>
                            <span className="ml-2 font-medium capitalize">{massnahme.status}</span>
                          </div>
                        </div>
                      </div>
                      {index < daten.massnahmenpakete.length - 1 && (
                        <div className="absolute left-5 mt-10 w-0.5 h-16 bg-gray-300"></div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default SanierungsTab;

